import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { WebtoonReader } from "@/components/site/WebtoonReader";
import { EASE } from "@/components/site/SectionHead";
import { api, asset } from "@/lib/api";

export default function WebtoonPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/webtoons/${id}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [id]);

  const episodes = Array.isArray(data?.episodes) ? data.episodes : [];
  const epNum = Number(params.get("ep"));
  const episode = episodes.find((e) => e.number === epNum) || episodes[0];
  const select = (e) => setParams({ ep: String(e.number) }, { replace: true });

  return (
    <main data-testid="webtoon-page" className="bg-[#0B0C10] text-zinc-100 min-h-screen">
      <Nav />
      <div className="px-5 sm:px-10 lg:px-16 pt-28">
        <Link to="/" state={{ scrollTo: "webtoons" }} data-testid="webtoon-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-[#F5A524] hover:gap-4 transition-all">
          <ArrowLeft size={14} /> ALL WEBTOONS
        </Link>
      </div>
      {error && <p className="px-5 sm:px-10 lg:px-16 py-20 font-mono text-zinc-400">SERIES NOT FOUND // 404</p>}
      {data && (
        <section className="px-5 sm:px-10 lg:px-16 pt-10 pb-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28 space-y-6">
            <p className="font-mono text-xs tracking-[0.35em] text-[#F5A524]">// WEBTOON SERIES</p>
            <h1 data-testid="webtoon-title" className="font-display font-black uppercase leading-[0.85] tracking-tight text-[clamp(3rem,7vw,6.5rem)]">
              <span className="block overflow-hidden">
                <motion.span className="block" initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: EASE }}>{data.series?.title}</motion.span>
              </span>
            </h1>
            <p className="text-base text-zinc-300 leading-relaxed whitespace-pre-line">{data.series?.description}</p>
            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] text-zinc-400 mb-3">EPISODES ({episodes.length})</p>
              <div data-lenis-prevent className="space-y-2 max-h-[46vh] overflow-y-auto pr-1">
                {episodes.map((e) => (
                  <button key={e.id} data-testid={`webtoon-ep-${e.number}`} onClick={() => select(e)}
                    className={`w-full flex items-center gap-3 p-2 border text-left transition-colors ${e.id === episode?.id ? "border-[#F5A524] bg-[#F5A524]/10" : "border-white/10 hover:border-[#F5A524]/50"}`}>
                    {e.pages?.[0] && <img src={asset(e.pages[0])} alt="" className="w-12 h-12 object-cover object-top shrink-0" />}
                    <span className="font-mono text-xs text-[#F5A524]">EP {String(e.number).padStart(2, "0")}</span>
                    <span className="text-sm text-zinc-200 truncate">{e.title}</span>
                  </button>
                ))}
                {episodes.length === 0 && <p className="font-mono text-xs text-zinc-400">NO EPISODES YET.</p>}
              </div>
            </div>
          </div>
          <div className="lg:col-span-8">
            {episode && <WebtoonReader episode={episode} episodes={episodes} onSelect={select} />}
          </div>
        </section>
      )}
      <Footer />
    </main>
  );
}
